import {
  getDisplayCurrency,
  setDisplayCurrency,
  subscribeDisplayCurrencyChanged,
} from '@experience/core-display-currency-lib';
import { type CurrencyCode, getCurrencySymbolSafe } from '@/shared/lib/currency';
import { normalizeDisplayCurrencyIso } from '@/shared/lib/normalizeDisplayCurrencyIso';
import type { DisplayCurrencyIso } from '@/shared/types/displayCurrency';
import { type MaybeRefOrGetter, computed, ref, toValue } from 'vue';
import { useDisplayCurrencies } from './useDisplayCurrencies';
import { useDisplayCurrencyQueryParams } from './useDisplayCurrencyQueryParams';

const appliedIso = ref<string | null>(normalizeDisplayCurrencyIso(getDisplayCurrency()));
const draftIso = ref<string | null>(appliedIso.value);

subscribeDisplayCurrencyChanged((currency) => {
  const normalized = normalizeDisplayCurrencyIso(currency);
  appliedIso.value = normalized;
  draftIso.value = normalized;
});

const findCurrency = (iso: DisplayCurrencyIso, currencies: { iso_code: string; symbol: string }[]) => {
  const normalized = normalizeDisplayCurrencyIso(iso);

  if (!normalized) {
    return undefined;
  }

  return currencies.find((currency) => normalizeDisplayCurrencyIso(currency.iso_code) === normalized);
};

export function useDisplayedCurrency(fallbackCurrencyCode: MaybeRefOrGetter<CurrencyCode>) {
  const { displayCurrencies } = useDisplayCurrencies();

  const resolveCurrencyCode = (iso: DisplayCurrencyIso): CurrencyCode => {
    const currency = findCurrency(iso, displayCurrencies.value);

    if (currency?.iso_code) {
      return currency.iso_code as CurrencyCode;
    }

    const normalized = normalizeDisplayCurrencyIso(iso);

    if (normalized) {
      return normalized as CurrencyCode;
    }

    return toValue(fallbackCurrencyCode);
  };

  const resolveCurrencySymbol = (iso: DisplayCurrencyIso) => {
    const currency = findCurrency(iso, displayCurrencies.value);

    if (currency?.symbol) {
      return currency.symbol;
    }

    const normalized = normalizeDisplayCurrencyIso(iso);

    if (normalized) {
      return normalized;
    }

    return getCurrencySymbolSafe(toValue(fallbackCurrencyCode));
  };

  const draftCurrencyCode = computed((): CurrencyCode => resolveCurrencyCode(draftIso.value));
  const draftCurrencySymbol = computed(() => resolveCurrencySymbol(draftIso.value));

  const selectDraftCurrency = (isoCode: string): boolean => {
    const normalized = normalizeDisplayCurrencyIso(isoCode);

    if (!normalized || normalizeDisplayCurrencyIso(draftIso.value) === normalized) {
      return false;
    }

    draftIso.value = normalized;

    return true;
  };

  return {
    displayCurrencies,
    draftIso,
    draftCurrencyCode,
    draftCurrencySymbol,
    appliedCurrencyCode: computed((): CurrencyCode => resolveCurrencyCode(appliedIso.value)),
    appliedCurrencySymbol: computed(() => resolveCurrencySymbol(appliedIso.value)),
    selectDraftCurrency,
  };
}

/** Query-параметры display_currency из черновика модалки (если отличается от применённой). */
export function useDraftDisplayCurrencyQueryParams() {
  const appliedQueryParams = useDisplayCurrencyQueryParams();

  return computed(() => {
    const draft = normalizeDisplayCurrencyIso(draftIso.value);
    const applied = normalizeDisplayCurrencyIso(appliedIso.value);

    if (draft && draft !== applied) {
      return { display_currency: draft };
    }

    return appliedQueryParams.value;
  });
}

export const displayCurrencyDraftResetCount = ref(0);

/** Сбросить черновик валюты к применённой (открытие/закрытие модалки без Apply). */
export function resetDisplayCurrencyDraft() {
  draftIso.value = appliedIso.value;
  displayCurrencyDraftResetCount.value += 1;
}

/** Применить черновик валюты: сохранить в localStorage через setDisplayCurrency. */
export function commitDisplayCurrencyChange(): boolean {
  const draft = normalizeDisplayCurrencyIso(draftIso.value);
  const applied = normalizeDisplayCurrencyIso(appliedIso.value);

  if (!draft || draft === applied) {
    return false;
  }

  setDisplayCurrency(draftIso.value!);
  appliedIso.value = draft;

  return true;
}
